import type { AssessmentScores } from '@core/ai/interviewAssessment';
import { supabase } from '../client';

function normalizeAssessmentScoresRow(raw: unknown): AssessmentScores | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const out: AssessmentScores = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const n = Number(v);
    if (Number.isFinite(n)) out[k] = n;
  }
  return Object.keys(out).length > 0 ? out : null;
}

export type AiInterviewSessionType = 'screening' | 'technical' | 'behavioral';
export type AiInterviewLanguage = string;

export type AiInterviewStoredMessage = {
  role: 'user' | 'assistant';
  content: string;
  at: string;
};

/** Snapshot pushed onto `prior_attempts` before each retake (same magic link). */
export type AiInterviewPriorAttempt = {
  archived_at: string;
  messages: AiInterviewStoredMessage[];
  recording_storage_path: string | null;
};

export type AiInterviewSessionRow = {
  id: string;
  access_token: string;
  language: string;
  session_type: AiInterviewSessionType;
  status: string;
  created_at: string;
  expires_at: string | null;
};

/** Who created the session link (ggj_b2b_org_users). */
export type AiInterviewInitiator = {
  id: string;
  display_name: string | null;
  email: string | null;
};

/** Session row with transcript + recording path (candidate profile / recruiter review). */
export type AiInterviewSessionWithArtifacts = AiInterviewSessionRow & {
  completed_at: string | null;
  messages: AiInterviewStoredMessage[];
  recording_storage_path: string | null;
  /** Snapshots from earlier attempts before retake (oldest first). */
  prior_attempts: AiInterviewPriorAttempt[];
  completed_too_short: boolean;
  created_by_org_user_id: string | null;
  initiator: AiInterviewInitiator | null;
  application_id: string | null;
  job_id: string | null;
};

/** Private Supabase Storage bucket for interview WebM uploads (org-scoped paths). */
export const AI_INTERVIEW_RECORDINGS_BUCKET = 'ggj_b2b-ai-interview-recordings';

function normalizeMessages(raw: unknown): AiInterviewStoredMessage[] {
  if (!Array.isArray(raw)) return [];
  const out: AiInterviewStoredMessage[] = [];
  for (const m of raw) {
    if (!m || typeof m !== 'object') continue;
    const role = (m as { role?: string }).role;
    const content = (m as { content?: string }).content;
    const at = (m as { at?: string }).at;
    if (role !== 'user' && role !== 'assistant') continue;
    if (typeof content !== 'string') continue;
    out.push({
      role,
      content,
      at: typeof at === 'string' && at.length > 0 ? at : new Date().toISOString(),
    });
  }
  return out;
}

function normalizePriorAttempts(raw: unknown): AiInterviewPriorAttempt[] {
  if (!Array.isArray(raw)) return [];
  const out: AiInterviewPriorAttempt[] = [];
  for (const a of raw) {
    if (!a || typeof a !== 'object') continue;
    const rec = a as Record<string, unknown>;
    const archived_at =
      typeof rec.archived_at === 'string' && rec.archived_at.length > 0 ? rec.archived_at : new Date().toISOString();
    const pathRaw = rec.recording_storage_path;
    const recording_storage_path =
      typeof pathRaw === 'string' && pathRaw.trim().length > 0 ? pathRaw.trim() : null;
    out.push({
      archived_at,
      messages: normalizeMessages(rec.messages),
      recording_storage_path,
    });
  }
  return out;
}

/** Pipeline calendar row paired with this AI session (optional). */
export type AiInterviewPipelineSlotSummary = {
  scheduled_at: string | null;
  status: string;
};

export type AiInterviewSessionListRow = AiInterviewSessionRow & {
  candidate_id: string;
  application_id: string | null;
  created_by_org_user_id: string | null;
  candidate: { full_name: string | null; email: string | null } | null;
  initiator: AiInterviewInitiator | null;
  pipeline_interview_event_id: string | null;
  pipeline_interview_event: AiInterviewPipelineSlotSummary | null;
  assessment_scores: AssessmentScores | null;
  assessed_at: string | null;
};

/** Hub list row plus fields needed for drill-down (application scope for email / calendar tabs). */
export type AiInterviewSessionHubDetailRow = AiInterviewSessionListRow & {
  application_id: string | null;
  job_id: string | null;
  completed_at: string | null;
  pipeline_interview_event_id: string | null;
  pipeline_interview_event: AiInterviewPipelineSlotSummary | null;
  /** Server-maintained; no separate `updated_by` column on this table. */
  updated_at: string;
  messages: AiInterviewStoredMessage[];
  prior_attempts: AiInterviewPriorAttempt[];
  recording_storage_path: string | null;
  completed_too_short: boolean;
  assessment_summary: string | null;
  assessment_rubric_version: string | null;
};

function randomAccessToken(): string {
  const buf = new Uint8Array(32);
  crypto.getRandomValues(buf);
  return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
}

export const aiInterviewSessionService = {
  /**
   * Sessions visible under org RLS. Transcript and optional recording live on the session row
   * (`messages`, `recording_storage_path`); recruiters open these from the dashboard.
   * UI may further filter so only **owner/admin** see every session; other roles often see sessions
   * they created (`created_by_org_user_id`).
   */
  async listForOrg(orgId: string, limit = 150): Promise<AiInterviewSessionListRow[]> {
    const { data, error } = await supabase
      .from('ggj_b2b_ai_interview_sessions')
      .select(
        `
        id,
        candidate_id,
        application_id,
        pipeline_interview_event_id,
        created_by_org_user_id,
        access_token,
        language,
        session_type,
        status,
        created_at,
        expires_at,
        assessment_scores,
        assessed_at,
        candidate:ggj_b2b_candidates(full_name, email),
        initiator:ggj_b2b_org_users!created_by_org_user_id(id, display_name, email),
        pipeline_interview_event:ggj_b2b_interview_events!pipeline_interview_event_id(scheduled_at, status)
      `
      )
      .eq('org_id', orgId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw new Error(error.message);
    return (data ?? []).map((row) => {
      const r = row as Record<string, unknown>;
      const raw = r.initiator as AiInterviewInitiator | null | undefined;
      const initiator =
        raw && typeof raw === 'object' && typeof (raw as AiInterviewInitiator).id === 'string'
          ? {
              id: (raw as AiInterviewInitiator).id,
              display_name: (raw as AiInterviewInitiator).display_name ?? null,
              email: (raw as AiInterviewInitiator).email ?? null,
            }
          : null;
      const peRaw = r.pipeline_interview_event;
      let pipeline_interview_event: AiInterviewPipelineSlotSummary | null = null;
      if (peRaw && typeof peRaw === 'object' && !Array.isArray(peRaw)) {
        const p = peRaw as Record<string, unknown>;
        pipeline_interview_event = {
          scheduled_at: (p.scheduled_at as string | null) ?? null,
          status: String(p.status ?? ''),
        };
      }
      return {
        ...r,
        initiator,
        application_id: (r.application_id as string | null) ?? null,
        pipeline_interview_event_id: (r.pipeline_interview_event_id as string | null) ?? null,
        pipeline_interview_event,
        assessment_scores: normalizeAssessmentScoresRow(r.assessment_scores),
        assessed_at: (r.assessed_at as string | null) ?? null,
      } as AiInterviewSessionListRow;
    });
  },

  /**
   * Single session for org (hub drill-down). Includes application/job ids when present.
   */
  async getByIdForOrg(orgId: string, sessionId: string): Promise<AiInterviewSessionHubDetailRow | null> {
    const { data, error } = await supabase
      .from('ggj_b2b_ai_interview_sessions')
      .select(
        `
        id,
        candidate_id,
        application_id,
        job_id,
        created_by_org_user_id,
        access_token,
        language,
        session_type,
        status,
        created_at,
        updated_at,
        expires_at,
        completed_at,
        pipeline_interview_event_id,
        messages,
        prior_attempts,
        recording_storage_path,
        completed_too_short,
        assessment_scores,
        assessment_summary,
        assessment_rubric_version,
        assessed_at,
        candidate:ggj_b2b_candidates(full_name, email),
        initiator:ggj_b2b_org_users!created_by_org_user_id(id, display_name, email),
        pipeline_interview_event:ggj_b2b_interview_events!pipeline_interview_event_id(scheduled_at, status)
      `
      )
      .eq('org_id', orgId)
      .eq('id', sessionId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!data || typeof data !== 'object') return null;
    const row = data as Record<string, unknown>;
    const raw = row.initiator as AiInterviewInitiator | null | undefined;
    const initiator =
      raw && typeof raw === 'object' && typeof (raw as AiInterviewInitiator).id === 'string'
        ? {
            id: (raw as AiInterviewInitiator).id,
            display_name: (raw as AiInterviewInitiator).display_name ?? null,
            email: (raw as AiInterviewInitiator).email ?? null,
          }
        : null;
    return {
      id: String(row.id),
      candidate_id: String(row.candidate_id),
      application_id: (row.application_id as string | null) ?? null,
      job_id: (row.job_id as string | null) ?? null,
      created_by_org_user_id: (row.created_by_org_user_id as string | null) ?? null,
      access_token: String(row.access_token),
      language: String(row.language ?? 'en-IN'),
      session_type: row.session_type as AiInterviewSessionType,
      status: String(row.status ?? ''),
      created_at: String(row.created_at ?? ''),
      updated_at: String(row.updated_at ?? row.created_at ?? ''),
      expires_at: (row.expires_at as string | null) ?? null,
      completed_at: (row.completed_at as string | null) ?? null,
      pipeline_interview_event_id: (row.pipeline_interview_event_id as string | null) ?? null,
      candidate: (row.candidate as { full_name: string | null; email: string | null } | null) ?? null,
      initiator,
      pipeline_interview_event: (() => {
        const peRaw = row.pipeline_interview_event;
        if (!peRaw || typeof peRaw !== 'object' || Array.isArray(peRaw)) return null;
        const p = peRaw as Record<string, unknown>;
        return {
          scheduled_at: (p.scheduled_at as string | null) ?? null,
          status: String(p.status ?? ''),
        };
      })(),
      assessment_scores: normalizeAssessmentScoresRow(row.assessment_scores),
      assessed_at: (row.assessed_at as string | null) ?? null,
      messages: normalizeMessages(row.messages),
      prior_attempts: normalizePriorAttempts(row.prior_attempts),
      recording_storage_path: (row.recording_storage_path as string | null) ?? null,
      completed_too_short: Boolean(row.completed_too_short),
      assessment_summary: typeof row.assessment_summary === 'string' ? row.assessment_summary : null,
      assessment_rubric_version:
        typeof row.assessment_rubric_version === 'string' ? row.assessment_rubric_version : null,
    };
  },

  async deleteByIds(orgId: string, sessionIds: string[]): Promise<void> {
    if (sessionIds.length === 0) return;
    const { error } = await supabase
      .from('ggj_b2b_ai_interview_sessions')
      .delete()
      .eq('org_id', orgId)
      .in('id', sessionIds);
    if (error) throw new Error(error.message);
  },

  async listForCandidate(orgId: string, candidateId: string, limit = 20): Promise<AiInterviewSessionWithArtifacts[]> {
    const { data, error } = await supabase
      .from('ggj_b2b_ai_interview_sessions')
      .select(
        'id, access_token, language, session_type, status, created_at, expires_at, completed_at, messages, recording_storage_path, prior_attempts, completed_too_short, created_by_org_user_id, application_id, job_id, initiator:ggj_b2b_org_users!created_by_org_user_id(id, display_name, email)',
      )
      .eq('org_id', orgId)
      .eq('candidate_id', candidateId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw new Error(error.message);
    return (data ?? []).map((row) => {
      const r = row as Record<string, unknown>;
      const raw = r.initiator as AiInterviewInitiator | null | undefined;
      const initiator =
        raw && typeof raw === 'object' && typeof (raw as AiInterviewInitiator).id === 'string'
          ? {
              id: (raw as AiInterviewInitiator).id,
              display_name: (raw as AiInterviewInitiator).display_name ?? null,
              email: (raw as AiInterviewInitiator).email ?? null,
            }
          : null;
      return {
        id: String(r.id),
        access_token: String(r.access_token),
        language: String(r.language ?? 'en-IN'),
        session_type: r.session_type as AiInterviewSessionType,
        status: String(r.status ?? ''),
        created_at: String(r.created_at ?? ''),
        expires_at: (r.expires_at as string | null) ?? null,
        completed_at: (r.completed_at as string | null) ?? null,
        messages: normalizeMessages(r.messages),
        recording_storage_path: (r.recording_storage_path as string | null) ?? null,
        prior_attempts: normalizePriorAttempts(r.prior_attempts),
        completed_too_short: Boolean(r.completed_too_short),
        created_by_org_user_id: (r.created_by_org_user_id as string | null) ?? null,
        application_id: (r.application_id as string | null) ?? null,
        job_id: (r.job_id as string | null) ?? null,
        initiator,
      };
    });
  },

  /** Signed URL for playback in the recruiter app (private bucket; RLS must allow SELECT). */
  async getRecordingSignedUrl(storagePath: string, expiresSec = 3600): Promise<string> {
    const { data, error } = await supabase.storage
      .from(AI_INTERVIEW_RECORDINGS_BUCKET)
      .createSignedUrl(storagePath, expiresSec);
    if (error) throw new Error(error.message);
    if (!data?.signedUrl) throw new Error('No signed URL returned');
    return data.signedUrl;
  },

  async createSession(params: {
    orgId: string;
    candidateId: string;
    jobId?: string | null;
    applicationId?: string | null;
    language: AiInterviewLanguage;
    sessionType: AiInterviewSessionType;
    createdByOrgUserId?: string | null;
    /** Pipeline interview row created in the same scheduling flow (calendar + tracked email). */
    pipelineInterviewEventId?: string | null;
    /** Default 14 days. */
    expiresInDays?: number | null;
  }): Promise<{ id: string; access_token: string }> {
    const expiresInDays = params.expiresInDays ?? 14;
    const expires_at =
      expiresInDays != null && expiresInDays > 0
        ? new Date(Date.now() + expiresInDays * 86400000).toISOString()
        : null;

    const access_token = randomAccessToken();

    const { data, error } = await supabase
      .from('ggj_b2b_ai_interview_sessions')
      .insert({
        org_id: params.orgId,
        candidate_id: params.candidateId,
        job_id: params.jobId ?? null,
        application_id: params.applicationId ?? null,
        pipeline_interview_event_id: params.pipelineInterviewEventId ?? null,
        created_by_org_user_id: params.createdByOrgUserId ?? null,
        access_token,
        language: params.language,
        session_type: params.sessionType,
        status: 'invited',
        expires_at,
      })
      .select('id, access_token')
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No row returned');
    return data as { id: string; access_token: string };
  },

  /** After creating a pipeline interview event (e.g. self-book with auto prep URL), attach it to this session. */
  async setPipelineInterviewEventId(orgId: string, sessionId: string, pipelineInterviewEventId: string): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_ai_interview_sessions')
      .update({ pipeline_interview_event_id: pipelineInterviewEventId })
      .eq('org_id', orgId)
      .eq('id', sessionId);
    if (error) throw new Error(error.message);
  },

  /**
   * C-24 §12 — Regenerate the AI summary with a different rubric template label.
   * The edge function re-runs the LLM assessment when
   * `VITE_AI_INTERVIEW_REGEN='edge'`; otherwise this path just marks
   * `assessment_rubric_version` with the template slug so the caller can
   * refetch the session row and show updated content.
   */
  async regenerateWithTemplate(orgId: string, sessionId: string, templateLabel: string): Promise<void> {
    // Stub: persist the chosen template label as rubric_version so downstream can read it.
    const { error } = await supabase
      .from('ggj_b2b_ai_interview_sessions')
      .update({ assessment_rubric_version: templateLabel.toLowerCase().replace(/\s+/g, '_') })
      .eq('org_id', orgId)
      .eq('id', sessionId);
    if (error) throw new Error(error.message);
  },
};
